import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './services/firebase';
import { AuthService } from './services/auth.service';
import { useAppStore } from './stores/useAppStore';
import { detectBrowserLanguage } from './utils/languageUtils';

// Error Boundary
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LoadingFallback } from './components/common/LoadingFallback';

// Support Chat Components
import { SupportChatButton } from './components/support/SupportChatButton';
import { SupportChatModal } from './components/support/SupportChatModal';
import { InstallPrompt } from './components/pwa/InstallPrompt';
import { OfflineBanner } from './components/pwa/OfflineBanner';

// Lazy load all route components for code splitting
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const PendingApproval = lazy(() => import('./components/auth/PendingApproval'));
const LandingPage = lazy(() => import('./components/pages/LandingPage'));
const TermsAndConditions = lazy(() => import('./components/legal/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));

const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const ManageClass = lazy(() =>
  import('./components/classroom/ManageClass').then((module) => ({ default: module.ManageClass }))
);
const AddClass = lazy(() => import('./components/classroom/AddClass'));
const Reports = lazy(() =>
  import('./components/classroom/Reports').then((module) => ({ default: module.Reports }))
);
const Schedule = lazy(() => import('./components/pages/Schedule'));
const Resources = lazy(() => import('./components/pages/Resources'));
const FAQ = lazy(() =>
  import('./components/pages/FAQ').then((module) => ({ default: module.FAQ }))
);
const HowTo = lazy(() =>
  import('./components/pages/HowTo').then((module) => ({ default: module.HowTo }))
);
const PublicClassView = lazy(() =>
  import('./components/pages/PublicClassView').then((module) => ({
    default: module.PublicClassView,
  }))
);

const UserProfile = lazy(() => import('./components/profile/UserProfile'));
const Settings = lazy(() => import('./components/pages/Settings'));
const SettingsNotifications = lazy(() => import('./components/settings/SettingsNotifications'));
const SettingsPrivacy = lazy(() => import('./components/settings/SettingsPrivacy'));

const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const SchoolDashboard = lazy(() => import('./components/admin/SchoolDashboard'));

// Student portal (separate auth, no teacher sidebar)
const StudentLogin = lazy(() => import('./components/student/StudentLogin'));
const StudentLayout = lazy(() => import('./components/student/StudentLayout'));
const XPDashboard = lazy(() => import('./components/student/XPDashboard'));
const ClassLeaderboard = lazy(() => import('./components/student/ClassLeaderboard'));
const BadgeShelf = lazy(() => import('./components/student/BadgeShelf'));
const StudentProfileView = lazy(() => import('./components/student/StudentProfileView'));
const StudentGuardLazy = lazy(() =>
  import('./components/student/StudentGuard').then((m) => ({ default: m.StudentGuardWithContext }))
);
const ForumSection = lazy(() => import('./components/student/ForumSection'));
const HomeworkList = lazy(() => import('./components/student/HomeworkList'));
const GrowthSection = lazy(() => import('./components/student/GrowthSection'));
const Grades = lazy(() =>
  import('./components/grades/Grades').then((module) => ({ default: module.Grades }))
);
const GamifiedClasses = lazy(() =>
  import('./components/gamified/GamifiedClasses').then((module) => ({
    default: module.GamifiedClasses,
  }))
);
const AIAssistant = lazy(() => import('./components/ai/AIAssistant'));
const PortalPage = lazy(() => import('./components/pages/PortalPage'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const Pricing = lazy(() => import('./components/pages/Pricing'));
const AdvocateLeaderboard = lazy(() => import('./components/pages/AdvocateLeaderboard'));
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard'));
const DisplayPage = lazy(() => import('./components/display/DisplayPage'));


const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentTeacher = useAppStore((state) => state.currentTeacher);
  const setCurrentTeacher = useAppStore((state) => state.setCurrentTeacher);
  const isLoading = useAppStore((state) => state.isLoading);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060A16]">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-white/10" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#5C7CFA] animate-spin" />
            <div className="absolute inset-4 rounded-full bg-[#5C7CFA]/20 flex items-center justify-center">
              <span className="text-xl leading-none">⚡</span>
            </div>
          </div>
          <p className="text-white/60 text-sm font-medium tracking-wide">Loading…</p>
        </div>
      </div>
    );
  }

  if (!currentTeacher) {
    return <Navigate to="/portal" />;
  }

  return (
    <>
      {children}
      {currentTeacher && !currentTeacher.onboardingCompleted && (
        <Suspense fallback={null}>
          <OnboardingWizard
            teacher={currentTeacher}
            onComplete={() => setCurrentTeacher({ ...currentTeacher, onboardingCompleted: true })}
          />
        </Suspense>
      )}
      <SupportChatButton onClick={() => setIsSupportChatOpen(true)} />
      <SupportChatModal
        isOpen={isSupportChatOpen}
        onClose={() => setIsSupportChatOpen(false)}
        teacherId={currentTeacher.uid}
      />
    </>
  );
};

const App: React.FC = () => {
  const setCurrentTeacher = useAppStore((state) => state.setCurrentTeacher);
  const setLoading = useAppStore((state) => state.setLoading);
  const theme = useAppStore((state) => state.theme);
  const setLanguage = useAppStore((state) => state.setLanguage);
  const currentTeacher = useAppStore((state) => state.currentTeacher);

  // Refresh session lastSeen every 5 minutes while logged in
  useEffect(() => {
    if (!currentTeacher) return;
    const interval = setInterval(() => {
      AuthService.refreshSessionLastSeen(currentTeacher.uid).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentTeacher?.uid]);

  // Auto-detect browser language on first visit (when no preference is stored yet)
  useEffect(() => {
    const stored = localStorage.getItem('app-storage');
    if (!stored) {
      const detected = detectBrowserLanguage();
      setLanguage(detected);
    }
  }, [setLanguage]);

  useEffect(() => {
    document.documentElement.classList.remove('theme-light', 'theme-dark', 'theme-neon');
    document.documentElement.classList.add(`theme-${theme}`);

    const bodyBg = theme === 'dark' ? 'bg-[#060A16]'
      : theme === 'neon' ? 'bg-[#050816]'
      : 'bg-[#F8FAFC]';
    document.body.className = `${bodyBg} transition-colors duration-300`;
  }, [theme]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const teacher = await AuthService.getCurrentTeacher(user);
        setCurrentTeacher(teacher);
      } else {
        setCurrentTeacher(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setCurrentTeacher, setLoading]);

  return (
    <ErrorBoundary>
      <Router>
        <OfflineBanner />
        <InstallPrompt />
        <AppRoutes />
      </Router>
    </ErrorBoundary>
  );
};

const AppRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <>
    <Suspense fallback={<LoadingFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.key}>
          {/* Student portal — separate auth, no teacher sidebar */}
          <Route path="/s/login" element={<StudentLogin />} />
            <Route
              path="/s"
              element={
                <StudentGuardLazy>
                  <StudentLayout />
                </StudentGuardLazy>
              }
            >
              <Route index element={<XPDashboard />} />
              <Route path="leaderboard" element={<ClassLeaderboard />} />
              <Route path="badges" element={<BadgeShelf />} />
              <Route path="profile" element={<StudentProfileView />} />
              <Route path="forum" element={<ForumSection />} />
              <Route path="homework" element={<HomeworkList />} />
              <Route path="growth" element={<GrowthSection />} />
            </Route>

            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/portal" element={<PortalPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/public/:shareToken" element={<PublicClassView />} />
            <Route path="/display/:sessionCode" element={<DisplayPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected main routes */}
            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <Dashboard />
                </AuthGuard>
              }
            />

            <Route
              path="/reports"
              element={
                <AuthGuard>
                  <Reports />
                </AuthGuard>
              }
            />

            <Route
              path="/schedule"
              element={
                <AuthGuard>
                  <Schedule />
                </AuthGuard>
              }
            />

            <Route
              path="/resources"
              element={
                <AuthGuard>
                  <Resources />
                </AuthGuard>
              }
            />

            <Route
              path="/faq"
              element={
                <AuthGuard>
                  <FAQ />
                </AuthGuard>
              }
            />

            <Route
              path="/how-to"
              element={
                <AuthGuard>
                  <HowTo />
                </AuthGuard>
              }
            />

            <Route
              path="/grades"
              element={
                <AuthGuard>
                  <Grades />
                </AuthGuard>
              }
            />

            <Route
              path="/gamified"
              element={
                <AuthGuard>
                  <GamifiedClasses />
                </AuthGuard>
              }
            />

            <Route
              path="/ai-assistant"
              element={
                <AuthGuard>
                  <AIAssistant />
                </AuthGuard>
              }
            />

            {/* Classroom routes - FIXED: Added AuthGuard */}
            <Route
              path="/class/manage/:classId"
              element={
                <AuthGuard>
                  <ManageClass />
                </AuthGuard>
              }
            />

            <Route
              path="/class/add"
              element={
                <AuthGuard>
                  <AddClass />
                </AuthGuard>
              }
            />

            {/* Settings and profile routes */}
            <Route
              path="/profile"
              element={
                <AuthGuard>
                  <UserProfile />
                </AuthGuard>
              }
            />

            <Route
              path="/settings"
              element={
                <AuthGuard>
                  <Settings />
                </AuthGuard>
              }
            />

            <Route
              path="/settings/notifications"
              element={
                <AuthGuard>
                  <SettingsNotifications />
                </AuthGuard>
              }
            />

            <Route
              path="/settings/privacy"
              element={
                <AuthGuard>
                  <SettingsPrivacy />
                </AuthGuard>
              }
            />

            {/* Admin route */}
            <Route
              path="/admin"
              element={
                <AuthGuard>
                  <AdminDashboard />
                </AuthGuard>
              }
            />

            {/* School Admin route */}
            <Route
              path="/school-dashboard"
              element={
                <AuthGuard>
                  <SchoolDashboard />
                </AuthGuard>
              }
            />

            {/* Pricing page — public */}
            <Route path="/pricing" element={<Pricing />} />

            {/* Advocate leaderboard — public */}
            <Route path="/leaderboard" element={<AdvocateLeaderboard />} />

            {/* Redirect any unknown routes to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </>
  );
};

export default App;
